import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  Form,
  Formik,
  FormikBag
} from "formik";
import { useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  makeStyles,
  MenuItem
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import CSVUploadLimitError from "src/components/CSVUploadLimitError";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useTenant } from "src/components/LoggedInAdminProvider";
import FilePicker from "src/components/form/FilePicker";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { FormikSelect } from "src/components/form/Select";
import { DEFAULT_UPLOAD_ROW_LIMIT } from "src/util/csv";
import { CSVMetadata, parseCsv } from "src/util/csv";
import { getErrorMessage } from "src/util/errors";
import yup from "src/util/yup";

import CsvPreview from "./CsvPreview";

type FormValues = {
  file?: File | null;
  phoneNumberColumn: string;
};

type FormValuesWithFile = FormValues & { file: File };

const useStyles = makeStyles(theme => ({
  fileFieldWrapper: {
    marginBottom: theme.spacing(3)
  },
  warning: {
    marginBottom: theme.spacing(3)
  }
}));

export default function UploadOptOutsDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FormValuesWithFile) => Promise<void>;
}): JSX.Element {
  const tenant = useTenant();

  const classes = useStyles();

  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const [csvMetadata, setCsvMetadata] = useState<CSVMetadata | null>(null);
  const columnNames = csvMetadata?.valid ? csvMetadata.columns : [];

  async function handleSubmit(values: FormValues) {
    setSaveErrors(null);
    try {
      await onSubmit(values as FormValuesWithFile);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  }

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>Upload Do-Not-Call CSV</DialogTitle>
      <Formik
        initialValues={{ file: null, phoneNumberColumn: "" }}
        validationSchema={yup.object().shape({
          file: yup.mixed().required(),
          phoneNumberColumn: yup
            .string()
            .required("Required")
            .oneOf(columnNames)
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Alert severity="warning" className={classes.warning}>
                <strong>Warning:</strong> To re-enroll numbers you’ve mistakenly
                opted out, you will have to either clear all opt-outs and
                re-upload, or manually re-enroll those numbers one at a time.
              </Alert>
              <FormikErrorBanner
                renderBanner={errors => {
                  if (errors.saveErrors) {
                    return `Error importing opt outs: ${errors.saveErrors}`;
                  }
                  return "Please fix the errors indicated below.";
                }}
                additionalErrors={saveErrors}
              />
              <div className={classes.fileFieldWrapper}>
                <Field name="file">
                  {({
                    field,
                    meta,
                    form
                  }: {
                    field: FieldInputProps<File>;
                    meta: FieldMetaProps<File>;
                    form: FormikBag<unknown, unknown>;
                  }) => {
                    async function handleSelectFile(file: File | null) {
                      form.setFieldValue("file", file);
                      if (file) {
                        const meta = await parseCsv(file);
                        if (!meta.valid) {
                          form.setFieldError(
                            "file",
                            getErrorMessage(meta.errors)
                          );
                        }
                        setCsvMetadata(meta);
                      } else {
                        setCsvMetadata(null);
                      }
                    }

                    if (field.value) {
                      if (!csvMetadata) {
                        return <LoadingIndicator />;
                      }

                      return (
                        <ParsedFile
                          file={field.value}
                          csvMetadata={csvMetadata}
                          onRemove={() => handleSelectFile(null)}
                        />
                      );
                    }
                    const error = meta.touched ? meta.error : null;
                    return (
                      <>
                        <FilePicker
                          dropzoneText="Drop a CSV here or click to select a file"
                          onPickFile={handleSelectFile}
                          maxSizeMB={tenant?.csvUploadLimitMb}
                          requiredMimeType={"text/csv"}
                          requiredMimeTypeDescription={"CSV"}
                        />
                        {error ? (
                          <FormHelperText error>{error}</FormHelperText>
                        ) : null}
                      </>
                    );
                  }}
                </Field>
              </div>
              <FormikSelect
                name="phoneNumberColumn"
                disabled={!columnNames.length}
                label="Select a phone number column"
                fullWidth
              >
                {columnNames.map(column => (
                  <MenuItem key={column} value={column}>
                    {column}
                  </MenuItem>
                ))}
              </FormikSelect>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Starting import..." : "Import"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function ParsedFile({
  csvMetadata,
  file,
  onRemove
}: {
  csvMetadata: CSVMetadata;
  file: File;
  onRemove: () => void;
}) {
  const tenant = useTenant();
  const uploadRowLimit = tenant?.csvUploadLimitRows || DEFAULT_UPLOAD_ROW_LIMIT;
  return (
    <Card>
      <CardHeader
        action={
          <IconButton onClick={onRemove}>
            <Delete />
          </IconButton>
        }
        title={file.name}
      />
      <CardContent>
        {csvMetadata.valid ? (
          csvMetadata.rows <= uploadRowLimit ? (
            <CsvPreview csvMetadata={csvMetadata} />
          ) : (
            <CSVUploadLimitError uploadRowLimit={uploadRowLimit} />
          )
        ) : (
          <>
            <p>
              <strong>Invalid CSV</strong>
            </p>
            <ul>
              {csvMetadata.errors.map(e => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
